import React from "react";
import { supabase } from "./supabaseClient";
import {
  getThemeColorForRawCategory,
  getCategoryAbbrForRawCategory,
  getCategoryDisplayLabel,
  getSubcategoryDisplayName,
  isListingHiddenFromMarketplace,
} from "./categoryTheme";
import {
  buildStoreUrl,
  copyToClipboard,
  formatStoreLocation,
} from "./storeLinks";
import { ChevronLeft, MapPin, Copy, Check, Lock, Store } from "lucide-react";

// Only what a visitor needs to see a store header. Phone/WhatsApp are added
// for signed-in buyers only (the product screen's contact sheet needs them).
const PUBLIC_SELLER_COLS =
  "id, display_name, avatar_id, category, university, location, county, status_type";
const FULL_SELLER_COLS = `${PUBLIC_SELLER_COLS}, phone_number, whatsapp_number`;

// A store is found ONLY by its random store code. (There used to be a
// fallback that also accepted a seller's account id; it's gone so account ids
// are never a way into a store.)
async function findSeller(code: string, cols: string): Promise<any | null> {
  const { data, error } = await supabase
    .from("seller_applications")
    .select(cols)
    .eq("store_code", code)
    .maybeSingle();
  return !error && data ? data : null;
}

type StorePageProps = {
  storeCode: string;
  onBack: () => void;
  onSignIn: () => void;
  onProductClick: (listing: any) => void;
  getAvatarColor: (avatarId?: string) => string;
};

export default function StorePage({
  storeCode,
  onBack,
  onSignIn,
  onProductClick,
  getAvatarColor,
}: StorePageProps) {
  const [authState, setAuthState] = React.useState<"loading" | "in" | "out">(
    "loading"
  );
  const [seller, setSeller] = React.useState<any | null>(null);
  const [listings, setListings] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // Track sign-in state (the Google redirect reloads the page, but this also
  // covers a session appearing/disappearing while the page is open).
  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthState(session ? "in" : "out");
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthState(session ? "in" : "out");
    });
    return () => subscription.unsubscribe();
  }, []);

  React.useEffect(() => {
    if (authState === "loading") return;
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setNotFound(false);
      try {
        const signedIn = authState === "in";
        const row = await findSeller(
          storeCode,
          signedIn ? FULL_SELLER_COLS : PUBLIC_SELLER_COLS
        );

        if (cancelled) return;
        if (!row || String(row.status_type || "").toLowerCase() !== "approved") {
          // Signed-out visitors may simply be blocked by database rules, so
          // they still get the generic sign-in teaser rather than "not found".
          setSeller(null);
          setListings([]);
          setNotFound(signedIn);
          return;
        }
        setSeller(row);

        if (!signedIn) {
          // Listings are never fetched for signed-out visitors — they only
          // see a blurred placeholder and the sign-in prompt.
          setListings([]);
          return;
        }

        const { data, error } = await supabase
          .from("listings")
          .select("*")
          .eq("seller_id", row.id)
          .eq("status", "active")
          .order("created_at", { ascending: false });
        if (cancelled) return;
        if (error) throw error;

        setListings(
          (data || [])
            .filter((l: any) => !isListingHiddenFromMarketplace(l))
            // The product screen reads seller details off this embedded object.
            .map((l: any) => ({ ...l, seller_applications: row }))
        );
      } catch (err) {
        console.error("[Store] failed to load store:", err);
        if (!cancelled) {
          setSeller(null);
          setListings([]);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeCode, authState]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(buildStoreUrl(storeCode));
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sellerName = seller?.display_name || "INUA Seller";
  const locationLine = formatStoreLocation(seller);
  const catKey = String(seller?.category || "").toLowerCase();
  const accent = getThemeColorForRawCategory(catKey);
  const signedOut = authState === "out";

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[var(--bg-app)] text-[var(--text-primary)] pb-16">
      {/* TOP BAR */}
      <div className="sticky top-0 z-30 flex items-center gap-2 px-3 py-3 bg-[var(--bg-header)]/95 backdrop-blur border-b border-[var(--border-soft)]">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="p-2 -ml-1 rounded-xl hover:bg-[var(--surface-soft-hover)] transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-bold text-base flex-1 truncate">Store</h1>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-soft)] hover:bg-[var(--surface-soft-hover)] transition-colors"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      {/* SELLER HEADER */}
      <div className="px-4 pt-5">
        {authState === "loading" || (isLoading && !seller) ? (
          <div className="flex items-center gap-4 animate-pulse">
            <div className="w-16 h-16 rounded-full bg-[var(--surface-soft)]" />
            <div className="flex-1 space-y-2">
              <div className="h-5 w-40 rounded bg-[var(--surface-soft)]" />
              <div className="h-3 w-56 rounded bg-[var(--surface-soft)]" />
            </div>
          </div>
        ) : notFound ? (
          <div className="py-20 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[var(--surface-soft)] flex items-center justify-center mb-4">
              <Store size={24} className="text-[var(--text-secondary)]" />
            </div>
            <p className="font-bold">This store isn't available</p>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              The link may be wrong, or the seller is no longer active.
            </p>
            <button
              type="button"
              onClick={onBack}
              className="mt-5 px-5 py-2.5 rounded-xl bg-[#FFB800] text-black text-sm font-bold active:scale-95 transition-transform"
            >
              Back to INUA
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div
              className={`w-16 h-16 rounded-full ${
                seller ? getAvatarColor(seller.avatar_id) : "bg-stone-600"
              } flex items-center justify-center font-bold text-white text-2xl shadow-md border border-white/20 shrink-0`}
            >
              {sellerName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-extrabold leading-tight break-words">
                {sellerName}
              </h2>
              {locationLine && (
                <p className="flex items-center gap-1 text-xs text-[var(--text-secondary)] mt-1">
                  <MapPin size={12} className="shrink-0" />
                  <span className="break-words">{locationLine}</span>
                </p>
              )}
              {seller?.category && (
                <span
                  className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-widest"
                  style={{
                    backgroundColor: `${accent}1A`,
                    color: accent,
                    borderColor: `${accent}33`,
                  }}
                >
                  {getCategoryDisplayLabel(seller.category)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* LISTINGS */}
      {!notFound && authState !== "loading" && (
        <div className="px-4 mt-6">
          {signedOut ? (
            <div className="relative">
              {/* Placeholder cards only — no real listing data is loaded. */}
              <div
                className="grid grid-cols-2 gap-4 blur-md select-none pointer-events-none"
                aria-hidden="true"
              >
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-[var(--bg-container)] rounded-xl overflow-hidden border border-[var(--border-soft)]"
                  >
                    <div
                      className="h-32 w-full"
                      style={{ backgroundColor: `${accent}33` }}
                    />
                    <div className="p-3 space-y-2">
                      <div className="h-2.5 w-16 rounded bg-[var(--surface-soft-hover)]" />
                      <div className="h-3.5 w-24 rounded bg-[var(--surface-soft-hover)]" />
                      <div className="h-3 w-14 rounded bg-[var(--surface-soft-hover)]" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-start justify-center pt-10">
                <div className="mx-2 w-full max-w-xs p-5 rounded-2xl bg-[var(--bg-container)] border border-[var(--border-soft)] shadow-2xl text-center">
                  <div className="w-11 h-11 mx-auto rounded-full bg-[#FFB800]/15 flex items-center justify-center mb-3">
                    <Lock size={20} className="text-[#FFB800]" />
                  </div>
                  <p className="font-bold text-sm">
                    Sign in to see {seller ? `${sellerName}'s` : "the"}{" "}
                    listings
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1 mb-4">
                    INUA is for verified campus and community sellers. Sign in
                    to browse their full catalogue.
                  </p>
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="w-full py-2.5 rounded-xl bg-[#FFB800] hover:bg-[#E6A600] text-black text-sm font-bold active:scale-95 transition-all"
                  >
                    Sign in to continue
                  </button>
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <div className="grid grid-cols-2 gap-4 animate-pulse">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-52 rounded-xl bg-[var(--surface-soft)]"
                />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-bold text-sm">No active listings right now</p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Check back soon.
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs text-[var(--text-secondary)] mb-3 font-medium">
                {listings.length} active listing
                {listings.length === 1 ? "" : "s"}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {listings.map((listing: any) => {
                  const image =
                    listing.photo_urls?.[0] ||
                    (Array.isArray(listing.images)
                      ? listing.images[0]
                      : listing.images) ||
                    listing.image_url;
                  const subLabel =
                    getSubcategoryDisplayName(listing.subcategory) ||
                    getCategoryDisplayLabel(seller?.category || "");
                  const abbr = getCategoryAbbrForRawCategory(catKey);
                  return (
                    <div
                      key={listing.id}
                      onClick={() => onProductClick(listing)}
                      className="bg-[var(--bg-container)] rounded-xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform border border-[var(--border-soft)] relative group shadow-lg"
                    >
                      {abbr && (
                        <span
                          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-sm text-[10px]"
                          style={{
                            border: `1.5px solid ${accent}`,
                            color: accent,
                          }}
                        >
                          {abbr}
                        </span>
                      )}
                      <div
                        className="h-32 w-full flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: `${accent}20` }}
                      >
                        {image ? (
                          <img
                            src={image}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <span
                            className="font-bold tracking-widest opacity-30 text-xs uppercase"
                            style={{ color: accent }}
                          >
                            {subLabel}
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <p
                          className="text-[10px] font-bold font-mono mb-1 uppercase tracking-wider truncate"
                          style={{ color: accent }}
                        >
                          {subLabel}
                        </p>
                        <h4 className="font-bold text-sm leading-tight mb-2 truncate">
                          {listing.title}
                        </h4>
                        <p className="font-black text-sm" style={{ color: accent }}>
                          {listing.listing_kind === "service" ? "From " : ""}
                          {typeof listing.price === "number"
                            ? `KES ${listing.price.toLocaleString()}`
                            : listing.price}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
